import { StatusKey } from '@/Enums/Advertisers/StatusKey.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import SearchInput from '@/components/SearchInput';
import StatusCheckbox from '@/components/StatusCheckbox';
import IOSSwitch from '@/components/Switch';
import { ADVERTISERS_STATUS_LABELS, STATUS_FILTER_MAP } from '@/constants/advertisers';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectAdvertisersFilters } from '@/store/selectors/advertisers.selectors';
import { selectIsAdmin, selectIsSuperAdmin, selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { setFilters } from '@/store/slices/advertisers.slice';
import { Button } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, useCallback, useState } from 'react';
import FilterPopover from './FilterPopover';

interface IProps {
  total: number;
  handleCreationModalOpen: () => void;
}

const Toolbar = ({ total, handleCreationModalOpen }: IProps) => {
  const dispatch = useAppDispatch();

  const isAdmin = useAppSelector(selectIsAdmin);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const { isArchived, unassigned } = useAppSelector(selectAdvertisersFilters);

  const [statusFilter, setStatusFilter] = useState<StatusKey>(StatusKey.ALL);
  const [search, setSearch] = useState<string>('');

  const onArchivedChange = useCallback(() => {
    dispatch(setFilters({ isArchived: !isArchived }));
  }, [dispatch, setFilters, isArchived]);

  const onUnassignedChange = useCallback(() => {
    dispatch(setFilters({ unassigned: !unassigned }));
  }, [dispatch, setFilters, unassigned]);

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.ADVERTISERS));

  const handleStatusFilterChange = useCallback(
    (value: StatusKey) => {
      setStatusFilter(value);
      const active = STATUS_FILTER_MAP[value] ?? null;
      dispatch(setFilters({ active }));
    },
    [dispatch, setStatusFilter],
  );

  const updateSearchFilter = useCallback(
    debounce((newSearch: string) => {
      dispatch(setFilters({ search: newSearch }));
    }, 500),
    [dispatch],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearch = event.target?.value;
      setSearch(newSearch);
      updateSearchFilter(newSearch);
    },
    [updateSearchFilter],
  );

  return (
    <GridToolbarContainer sx={{ padding: 0 }}>
      <div className='p-[20px] relative w-full'>
        <div className='flex justify-between items-center'>
          <div className='mb-[10px]'>
            <h2 className='mb-[5px]'>
              <span className='text-[#171725] text-[24px] font-semibold'>Advertisers</span>
              <span className='text-[#C1BFC2] text-[20px]'>
                &nbsp;({numberToUsFormat(total, { maximumFractionDigits: 0 })})
              </span>
            </h2>
          </div>
          <div>
            <Button
              disabled={readOnly}
              sx={{ height: 40, width: 200 }}
              color='primary'
              variant='contained'
              onClick={handleCreationModalOpen}
            >
              + Create New Advertiser
            </Button>
          </div>
        </div>
        <div className='flex justify-between'>
          <div className='flex gap-[6px]'>
            {ADVERTISERS_STATUS_LABELS.map(({ name, label }) => (
              <StatusCheckbox
                key={name}
                label={label}
                onChange={() => handleStatusFilterChange(name)}
                name={name}
                checked={statusFilter === name}
              />
            ))}
          </div>
          <div className='table-controls flex gap-[10px]'>
            {isAdmin && (
              <Button variant='outlined' onClick={onArchivedChange} sx={{ height: '37px' }}>
                <span>Archived</span>
                <IOSSwitch sx={{ marginLeft: '7px' }} checked={isArchived} />
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant='outlined' onClick={onUnassignedChange} sx={{ height: '37px' }}>
                <span>No Client</span>
                <IOSSwitch
                  sx={{ marginLeft: '7px' }}
                  checked={unassigned}
                  inputProps={{ 'aria-label': 'No Client filter' }}
                />
              </Button>
            )}
            <SearchInput placeholder='Search..' value={search} onChange={onSearchChange} />
            <FilterPopover />
          </div>
        </div>
      </div>
    </GridToolbarContainer>
  );
};

export default Toolbar;
